import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Alternate product "bundle" card.
 *
 * Wraps a single product's `product-form-component`, reusing its add-to-cart
 * flow (POST, cart-drawer open, error handling) so this block ships no bespoke
 * add logic. On a successful add it permanently switches the Add button to its
 * "Added" state and disables it, so the single alternate product can only be
 * added once.
 *
 * The add button lives inside the nested `product-form-component`, so it cannot
 * be exposed as a `ref` on this component — the ref system stops at the nested
 * component boundary. We locate it with `querySelector` instead.
 *
 * @extends Component
 */
class AlternateProductCard extends Component {
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    }

    /**
     * Reacts only to this card's own successful add-to-cart. The event bubbles up
     * from the wrapped product-form-component, so it is already scoped to this
     * card — a different card's (or the main PDP's) add never reaches here.
     *
     * @param {Event} event
     */
    #onCartUpdate = (event) => {
        const data = /** @type {any} */ (event).detail?.data;
        if (data?.source !== 'product-form-component' || data?.didError) return;

        // product-form-component re-enables the submit button in its fetch
        // `finally()`, which runs after this synchronous event dispatch. Defer so
        // our disabled state is applied last and sticks.
        requestAnimationFrame(() => this.#markAdded());
    };

    #markAdded() {
        const addButton = /** @type {HTMLButtonElement | null} */ (
            this.querySelector('button[name="add"]')
        );
        if (!addButton) return;

        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');
        addButton.setAttribute('data-added', 'true');
    }
}

if (!customElements.get('alternate-product-card')) {
    customElements.define('alternate-product-card', AlternateProductCard);
}